(() => {
  const cartAutoUpdate = window.MXCartAutoUpdate;

  if (!cartAutoUpdate) {
    return;
  }

  const { CONFIG, SELECTORS, wait } = cartAutoUpdate;

  const splitLineText = (lineText) => {
    const colonIndex = lineText.indexOf(':');

    if (colonIndex === -1) {
      return {
        label: lineText,
        value: '',
      };
    }

    return {
      label: lineText.slice(0, colonIndex + 1),
      value: lineText.slice(colonIndex + 1),
    };
  };

  const animateValueOnly = async (line, fromText, toText) => {
    const labelNode = line.querySelector(SELECTORS.terminalLabel);
    const valueNode = line.querySelector(SELECTORS.terminalValue);
    const fromParts = splitLineText(fromText);
    const toParts = splitLineText(toText);

    if (!(valueNode instanceof HTMLElement)) {
      return;
    }

    line.classList.add('is-visible', 'is-typing');

    if (labelNode instanceof HTMLElement) {
      labelNode.textContent = toParts.label;
    }

    if (fromParts.label !== toParts.label) {
      valueNode.textContent = toParts.value;
      line.classList.remove('is-typing');
      return;
    }

    for (let cursor = fromParts.value.length; cursor >= 0; cursor -= 1) {
      valueNode.textContent = fromParts.value.slice(0, cursor);
      // eslint-disable-next-line no-await-in-loop
      await wait(CONFIG.terminalEraseDelayMs);
    }

    for (let cursor = 0; cursor <= toParts.value.length; cursor += 1) {
      valueNode.textContent = toParts.value.slice(0, cursor);
      // eslint-disable-next-line no-await-in-loop
      await wait(CONFIG.terminalTypeDelayMs);
    }

    line.classList.remove('is-typing');
  };

  const syncTerminal = async (currentCartPage, nextCartPage) => {
    const currentTerminal = currentCartPage.querySelector(SELECTORS.terminal);
    const nextTerminal = nextCartPage.querySelector(SELECTORS.terminal);

    if (
      !(currentTerminal instanceof HTMLElement) ||
      !(nextTerminal instanceof HTMLElement)
    ) {
      return;
    }

    const currentLines = Array.from(
      currentTerminal.querySelectorAll(SELECTORS.terminalLine),
    );
    const nextLines = Array.from(
      nextTerminal.querySelectorAll(SELECTORS.terminalLine),
    );

    if (!currentLines.length || currentLines.length !== nextLines.length) {
      currentTerminal.replaceWith(nextTerminal);

      if (typeof window.initializeTerminals === 'function') {
        window.initializeTerminals(currentCartPage);
      }

      return;
    }

    currentLines.forEach((line) => {
      line.classList.remove('terminal__line--active');
    });

    const activeIndex = nextLines.length - 1;

    if (activeIndex >= 0) {
      currentLines[activeIndex].classList.add('terminal__line--active');
    }

    for (let index = 0; index < currentLines.length; index += 1) {
      const line = currentLines[index];
      const currentText = line.getAttribute('data-line-text') || '';
      const nextText = nextLines[index].getAttribute('data-line-text') || '';

      if (currentText === nextText) {
        continue;
      }

      line.setAttribute('data-line-text', nextText);
      // eslint-disable-next-line no-await-in-loop
      await animateValueOnly(line, currentText, nextText);
    }
  };

  const syncFormDetails = (currentCartPage, nextCartPage) => {
    const currentForm = currentCartPage.querySelector(SELECTORS.cartForm);
    const nextForm = nextCartPage.querySelector(SELECTORS.cartForm);

    if (
      !(currentForm instanceof HTMLFormElement) ||
      !(nextForm instanceof HTMLFormElement)
    ) {
      return false;
    }

    const currentItems = Array.from(
      currentForm.querySelectorAll(SELECTORS.cartItem),
    );
    const nextItems = Array.from(nextForm.querySelectorAll(SELECTORS.cartItem));

    if (currentItems.length !== nextItems.length) {
      return false;
    }

    const currentSubtotal = currentForm.querySelector(SELECTORS.subtotalStrong);
    const nextSubtotal = nextForm.querySelector(SELECTORS.subtotalStrong);

    if (
      currentSubtotal instanceof HTMLElement &&
      nextSubtotal instanceof HTMLElement
    ) {
      currentSubtotal.textContent = nextSubtotal.textContent;
    }

    for (let index = 0; index < currentItems.length; index += 1) {
      const currentItem = currentItems[index];
      const nextItem = nextItems[index];
      const currentPrice = currentItem.querySelector(SELECTORS.cartItemPrice);
      const nextPrice = nextItem.querySelector(SELECTORS.cartItemPrice);
      const currentInput = currentItem.querySelector(SELECTORS.quantityInput);
      const nextInput = nextItem.querySelector(SELECTORS.quantityInput);

      if (
        currentPrice instanceof HTMLElement &&
        nextPrice instanceof HTMLElement
      ) {
        currentPrice.textContent = nextPrice.textContent;
      }

      if (
        currentInput instanceof HTMLInputElement &&
        nextInput instanceof HTMLInputElement
      ) {
        currentInput.value = nextInput.value;
      }
    }

    return true;
  };

  const getCartPageFromResponse = async () => {
    const response = await fetch(
      `${window.location.pathname}?section_id=${CONFIG.cartSectionId}`,
      {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      },
    );

    if (!response.ok) {
      throw new Error('Cart section refresh failed.');
    }

    const html = await response.text();
    const parser = new DOMParser();
    const nextDocument = parser.parseFromString(html, 'text/html');
    const nextCartPage = nextDocument.querySelector(SELECTORS.cartPage);

    if (!(nextCartPage instanceof HTMLElement)) {
      throw new Error('Cart page root not found.');
    }

    return nextCartPage;
  };

  const postCartUpdate = async (form) => {
    const formData = new FormData(form);

    formData.delete('checkout');
    formData.delete('update');

    const response = await fetch(form.action, {
      method: 'POST',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) {
      throw new Error('Cart update failed.');
    }
  };

  Object.assign(cartAutoUpdate, {
    syncTerminal,
    syncFormDetails,
    getCartPageFromResponse,
    postCartUpdate,
  });
})();
